import React, { useState, useEffect } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Mail, Check, Ban, Eye, MessageSquare } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const CLASS_COLORS = {
  interested: 'text-green-400 border-green-500/30 bg-green-500/10',
  request_link: 'text-blue-400 border-blue-500/30 bg-blue-500/10',
  objection_price: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
  objection_trust: 'text-orange-400 border-orange-500/30 bg-orange-500/10',
  not_now: 'text-slate-400 border-slate-600',
  unsubscribe: 'text-red-400 border-red-500/30 bg-red-500/10',
  wrong_person: 'text-slate-500 border-slate-700',
  other: 'text-slate-400 border-slate-600',
  unknown: 'text-slate-500 border-slate-700',
};

const ACTION_LABELS = {
  auto_reply: 'Auto Reply',
  needs_approval: 'Needs Approval',
  escalate: 'Escalate',
  close: 'Close',
  add_dnc: 'Add to DNC',
};

export default function BackofficeInbox() {
  const [replies, setReplies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unhandled');
  const [detail, setDetail] = useState(null);

  const load = (status) => {
    setLoading(true);
    api.get('/backoffice/inbox', { params: { handled_status: status || undefined, limit: 100 } })
      .then(r => { setReplies(r.data.replies || []); setTotal(r.data.total); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); }, [filter]);

  const approveReply = async (id) => {
    try {
      await api.post(`/backoffice/inbox/${id}/approve-reply`);
      toast.success('Reply approved and queued');
      setDetail(null);
      load(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const resolve = async (id) => {
    await api.post(`/backoffice/inbox/${id}/resolve`);
    toast.success('Resolved');
    setDetail(null);
    load(filter);
  };

  const addDnc = async (id) => {
    await api.post(`/backoffice/inbox/${id}/add-dnc`);
    toast.success('Added to DNC');
    setDetail(null);
    load(filter);
  };

  return (
    <BackofficeLayout>
      <div data-testid="backoffice-inbox">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            INBOX <span className="text-slate-500 font-mono text-sm ml-2">{total}</span>
          </h1>
        </div>

        <div className="flex gap-1 mb-4">
          {['unhandled', 'queued', 'resolved', ''].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${filter === s ? 'bg-slate-700 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
              data-testid={`inbox-filter-${s || 'all'}`}
            >
              {s || 'all'}
            </button>
          ))}
        </div>

        <div className="space-y-1">
          {loading ? (
            [...Array(3)].map((_, i) => <div key={i} className="h-14 bg-slate-900 border border-slate-800 rounded-sm animate-pulse" />)
          ) : replies.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-10 h-10 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No replies</p>
            </div>
          ) : (
            replies.map(r => (
              <button
                key={r.id}
                onClick={() => setDetail(r)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-900 border border-slate-800 rounded-sm hover:border-slate-600 transition-colors text-left"
                data-testid={`inbox-row-${r.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <MessageSquare className="w-4 h-4 text-slate-600 flex-shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate">{r.prospect_name || r.from_email}</p>
                    <p className="text-xs text-slate-500 truncate">{r.subject}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <Badge variant="outline" className={`text-[10px] ${CLASS_COLORS[r.classification] || ''}`}>
                    {r.classification}
                  </Badge>
                  <span className="text-[10px] font-mono text-slate-600">{Math.round((r.confidence || 0) * 100)}%</span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Detail Dialog */}
        <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-200 max-w-lg">
            {detail && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white">{detail.prospect_name || detail.from_email}</DialogTitle>
                  <DialogDescription className="text-slate-400 text-sm">Inbound reply details and suggested actions.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`text-[10px] ${CLASS_COLORS[detail.classification]}`}>{detail.classification}</Badge>
                    <span className="text-[10px] font-mono text-slate-500">confidence: {Math.round(detail.confidence * 100)}%</span>
                    <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-600">
                      {ACTION_LABELS[detail.action] || detail.action}
                    </Badge>
                  </div>

                  <div className="bg-slate-800 p-3 rounded-sm">
                    <p className="text-xs font-semibold text-slate-300 mb-1">Subject: {detail.subject}</p>
                    <p className="text-xs text-slate-400 whitespace-pre-wrap mt-2">{detail.body}</p>
                  </div>

                  {detail.suggested_reply_body && (
                    <div className="border border-dashed border-slate-700 p-3 rounded-sm">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Suggested Reply</p>
                      <p className="text-xs font-semibold text-slate-300 mb-1">{detail.suggested_reply_subject}</p>
                      <pre className="text-xs text-slate-400 whitespace-pre-wrap font-sans">{detail.suggested_reply_body}</pre>
                    </div>
                  )}
                </div>
                <DialogFooter className="flex gap-2">
                  {detail.suggested_reply_body && detail.handled_status === 'unhandled' && (
                    <Button className="bg-green-600 hover:bg-green-700 text-white rounded-sm" onClick={() => approveReply(detail.id)} data-testid="approve-reply-btn">
                      <Check className="w-4 h-4 mr-1" /> Approve Reply
                    </Button>
                  )}
                  {detail.handled_status === 'unhandled' && (
                    <>
                      <Button variant="ghost" className="text-slate-400" onClick={() => resolve(detail.id)}>Resolve</Button>
                      <Button variant="ghost" className="text-red-400" onClick={() => addDnc(detail.id)} data-testid="inbox-dnc-btn">
                        <Ban className="w-3 h-3 mr-1" /> DNC
                      </Button>
                    </>
                  )}
                  {detail.handled_status !== 'unhandled' && (
                    <Button variant="ghost" className="text-slate-400" onClick={() => setDetail(null)}>Close</Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </BackofficeLayout>
  );
}
